import useBeauty from 'hooks/beautyFilter/useBeauty';
import useMakeup from 'hooks/beautyFilter/useMakeup';
import useMood from 'hooks/beautyFilter/useMood';
import useSticker from 'hooks/beautyFilter/useSticker';
import useBanubaGuard from 'hooks/beautyFilter/useBanubaGuard';
// import useBackground from 'hooks/beautyFilter/useBackground';
import { CancelItem, IconItem, ThumbnailItem, StickerItem } from 'components/beautyFilter/BeautyFilterItems';

const BeautyFilterTabs: React.FC = () => {
  // 탭이 열린 채로 banubaUnavailable/gpuPassthrough 판정이 나는 경우를 대비해, 아이템 클릭을
  // capture 단계에서 가로채 토스트로 안내한다 (display: contents라 레이아웃 영향 없음).
  // 뷰티·메이크업은 두 판정 모두, 무드·스티커는 gpuPassthrough만 막는다.
  const { guardBanubaClick, guardPassthroughClick } = useBanubaGuard();
  return (
    <>
      <div className="contents" onClickCapture={guardBanubaClick}>
        <BeautyTab />
        <MakeupTab />
      </div>
      <div className="contents" onClickCapture={guardPassthroughClick}>
        <MoodTab />
        <StickerTab />
      </div>
      {/* <BackgroundTab /> */}
    </>
  );
};

export default BeautyFilterTabs;

const BeautyTab: React.FC = () => {
  const {
    keys,
    table,
    isTabVisible,
    handleItemAllReset,
    isItemSelected,
    isItemUsing,
    handleItemSelect,
    isSubTabVisible,
    handleSubItemAllReset,
    isSubItemSelected,
    isSubItemUsing,
    handleSubItemSelect,
  } = useBeauty();
  const isIOS = Device.name === 'iOS';
  const isRTL = BResource.isRTL();

  return (
    <>
      {/* Main Tab */}
      <div style={{ display: isTabVisible ? 'flex' : 'none' }} className="max-h-[120px] items-start gap-4 overflow-x-scroll px-4 pb-4">
        <CancelItem onClick={handleItemAllReset} />
        {keys.map((key) => (
          <IconItem
            key={key}
            thumbnail={table[key].thumbnail}
            brscName={table[key].brscName}
            isSelected={isItemSelected(key)}
            isUsing={isItemUsing(key)}
            onClick={() => handleItemSelect(key)}
          />
        ))}
        {isIOS && isRTL && <div className="h-[104px] pe-[0.1px]" /> /* iOS + RTL padding 버그 조치 */}
      </div>
      {/* Sub Tab */}
      {keys.map((key) => (
        <div key={key} style={{ display: isSubTabVisible(key) ? 'flex' : 'none' }} className="flex max-h-[120px] items-start gap-4 overflow-x-scroll px-4 pb-4">
          <CancelItem onClick={() => handleSubItemAllReset(key)} />
          {table[key].subKeys.map((subKey) => (
            <IconItem
              key={subKey}
              thumbnail={table[key].subTable[subKey].thumbnail}
              brscName={table[key].subTable[subKey].brscName}
              brscSubName={table[key].subTable[subKey].brscSubName}
              isSelected={isSubItemSelected(subKey)}
              isUsing={isSubItemUsing(key, subKey)}
              onClick={() => handleSubItemSelect(subKey)}
            />
          ))}
          {isIOS && isRTL && <div className="h-[104px] pe-[0.1px]" /> /* iOS + RTL padding 버그 조치 */}
        </div>
      ))}
    </>
  );
};

const MakeupTab: React.FC = () => {
  const {
    keys,
    table,
    isTabVisible,
    handleItemAllReset,
    isItemSelected,
    isItemUsing,
    handleItemSelect,
    isSubTabVisible,
    handleSubItemAllReset,
    isSubItemSelected,
    handleSubItemSelect,
  } = useMakeup();
  const isIOS = Device.name === 'iOS';
  const isRTL = BResource.isRTL();
  return (
    <>
      {/* Main Tab */}
      <div style={{ display: isTabVisible ? 'flex' : 'none' }} className="flex max-h-[120px] items-start gap-4 overflow-x-scroll px-4 pb-4">
        <CancelItem onClick={handleItemAllReset} />
        {keys.map((key) => (
          <IconItem
            key={key}
            thumbnail={table[key].thumbnail}
            brscName={table[key].brscName}
            isSelected={isItemSelected(key)}
            isUsing={isItemUsing(key)}
            onClick={() => handleItemSelect(key)}
          />
        ))}
        {isIOS && isRTL && <div className="h-[104px] pe-[0.1px]" /> /* iOS + RTL padding 버그 조치 */}
      </div>
      {/* Sub Tab */}
      {keys.map((key) => (
        <div key={key} style={{ display: isSubTabVisible(key) ? 'flex' : 'none' }} className="flex max-h-[120px] items-start gap-4 overflow-x-scroll px-4 pb-4">
          <CancelItem onClick={() => handleSubItemAllReset(key)} />
          {table[key].subKeys.map((subKey) => (
            <ThumbnailItem
              key={subKey}
              thumbnail={table[key].subTable[subKey].thumbnail}
              name={table[key].subTable[subKey].name}
              isSelected={isSubItemSelected(subKey)}
              onClick={() => handleSubItemSelect(subKey)}
            />
          ))}
          {isIOS && isRTL && <div className="h-[104px] pe-[0.1px]" /> /* iOS + RTL padding 버그 조치 */}
        </div>
      ))}
    </>
  );
};

const MoodTab: React.FC = () => {
  const { keys, table, isTabVisible, handleItemAllReset, isItemSelected, handleItemSelect } = useMood();
  const isIOS = Device.name === 'iOS';
  const isRTL = BResource.isRTL();
  return (
    <div style={{ display: isTabVisible ? 'flex' : 'none' }} className="flex max-h-[120px] items-start gap-4 overflow-x-scroll px-4 pb-4">
      <CancelItem onClick={handleItemAllReset} />
      {keys.map((key) => (
        <ThumbnailItem
          key={key}
          thumbnail={table[key].thumbnail}
          brscName={table[key].brscName}
          name={table[key].name}
          isSelected={isItemSelected(key)}
          onClick={() => handleItemSelect(key)}
        />
      ))}
      {isIOS && isRTL && <div className="h-[104px] pe-[0.1px]" /> /* iOS + RTL padding 버그 조치 */}
    </div>
  );
};

const StickerTab: React.FC = () => {
  const { keys, table, isTabVisible, handleItemAllReset, isItemSelected, handleItemSelect } = useSticker();
  const isIOS = Device.name === 'iOS';
  const isRTL = BResource.isRTL();
  return (
    <div style={{ display: isTabVisible ? 'grid' : 'none' }} className="grid max-h-[120px] grid-cols-4 gap-4 overflow-y-scroll px-4 pb-4">
      <CancelItem onClick={handleItemAllReset} />
      {keys.map((key) => (
        <StickerItem key={key} id={key} thumbnail={table[key].thumbnail} isSelected={isItemSelected(key)} onClick={() => handleItemSelect(key)} />
      ))}
      {isIOS && isRTL && <div className="h-[104px] pe-[0.1px]" /> /* iOS + RTL padding 버그 조치 */}
    </div>
  );
};

// const BackgroundTab: React.FC = () => {
//   const { keys, table, isTabVisible, handleItemAllReset, isItemSelected, handleItemSelect } = useBackground();

//   return (
//     <div style={{ display: isTabVisible() ? 'flex' : 'none' }} className="flex max-h-[120px] items-start gap-4 overflow-x-scroll px-4 pb-4">
//       <CancelItem onClick={() => handleItemAllReset()} />
//       {keys.map((key) => (
//         <ThumbnailItem
//           key={key}
//           thumbnail={table[key].thumbnail}
//           brscName={table[key].brscName}
//           isSelected={isItemSelected(key)}
//           onClick={() => handleItemSelect(key)}
//         />
//       ))}
//     </div>
//   );
// };
